<template>
  <AppShell>
    <section class="space-y-8">
      <div class="space-y-3">
        <p class="app-eyebrow">{{ t("paymentReturn.eyebrow", { provider: providerLabel }) }}</p>
        <h1 class="text-3xl font-bold tracking-tight">{{ display.title }}</h1>
        <p class="max-w-2xl text-sm leading-7 text-brand-muted-strong">
          {{ display.description }}
        </p>
      </div>

      <Card class="rounded-none border-brand-line bg-brand-surface shadow-none">
        <CardContent
          class="flex flex-col gap-6 p-8 md:flex-row md:items-center md:justify-between"
        >
          <div class="flex items-start gap-4">
            <span
              class="flex size-12 shrink-0 items-center justify-center rounded-full"
              :class="iconWrapperClass"
            >
              <component :is="statusIcon" class="size-6" />
            </span>
            <div class="space-y-2">
              <p class="app-eyebrow">{{ t("paymentReturn.status") }}</p>
              <p class="text-xl font-bold text-brand-ink">{{ display.title }}</p>
              <p v-if="confirmation.resultCode" class="text-sm text-brand-muted">
                {{
                  t("paymentReturn.result", {
                    provider: providerLabel,
                    result: confirmation.resultCode,
                  })
                }}
              </p>
              <p
                v-if="confirmation.orderReference"
                class="text-sm text-brand-muted"
              >
                {{
                  t("paymentReturn.order", {
                    reference: confirmation.orderReference,
                  })
                }}
              </p>
            </div>
          </div>

          <div class="grid justify-end gap-3">
            <Button
              class="h-12 rounded-none border-0 bg-brand-fill px-8 text-[12px] font-bold uppercase tracking-[0.14em] text-white shadow-none hover:bg-brand-fill-hover"
              @click="router.push({ name: 'token-purchase' })"
            >
              <ShoppingCart class="size-4" />
              {{ t("paymentReturn.toPurchase") }}
            </Button>
            <Button
              v-if="display.variant === 'error'"
              variant="outline"
              class="h-12 rounded-none border-brand-line bg-brand-surface px-8 text-[12px] font-bold uppercase tracking-[0.14em] text-brand-ink shadow-none hover:bg-brand-surface-muted"
              @click="router.push({ name: 'token-purchase' })"
            >
              {{ t("paymentReturn.retry") }}
            </Button>
          </div>
        </CardContent>
      </Card>
    </section>
  </AppShell>
</template>

<script setup lang="ts">
import { CurrentPaymentDtoStatus } from "@token/shared";
import { computed, onMounted, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { CircleCheck, Clock, ShoppingCart, ShieldAlert } from "lucide-vue-next";
import { toast } from "vue-sonner";
import AppShell from "@/components/AppShell.vue";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useCustomerSession } from "@/features/customer-session";
import { createPaymentApi, usePaymentProvider } from "@/features/payment";
import { useApi } from "@/lib/api";
import { paymentProviderLabel } from "@/lib/config";
import { useI18n } from "@/lib/i18n";

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const api = useApi();
const paymentProvider = usePaymentProvider();
const paymentApi = createPaymentApi(api);
const customerSession = useCustomerSession();
let pollingInterval: number | null = null;

const confirmation = paymentProvider.parseReturn({ query: route.query });
const providerLabel = computed(() => paymentProviderLabel(confirmation.provider));
const customer = computed(() => customerSession.current);
const returnedSessionId = computed(
  () => confirmation.paymentProviderSessionId ?? confirmation.sessionId,
);
const latestPayment = computed(() => customer.value?.latestPayment ?? null);
const latestPaymentRecord = computed(() => latestPayment.value?.payment ?? null);
const isLatestReturnedPayment = computed(() => {
  const sessionId = returnedSessionId.value;
  const payment = latestPaymentRecord.value;
  if (!payment) return false;
  return payment.paymentProviderSessionId === sessionId || payment.id === sessionId;
});
const latestPaymentStatus = computed(() => {
  if (!isLatestReturnedPayment.value) return undefined;
  return latestPayment.value?.status ?? latestPaymentRecord.value?.status;
});

const display = computed(() => {
  const provider = providerLabel.value;
  switch (latestPaymentStatus.value) {
    case CurrentPaymentDtoStatus.AUTHORISED:
    case CurrentPaymentDtoStatus.REFUNDED:
      return {
        variant: "success" as const,
        title: t("paymentReturn.successTitle"),
        description: t("paymentReturn.successDescription"),
      };
    case CurrentPaymentDtoStatus.SESSION_CREATED:
    case CurrentPaymentDtoStatus.SESSION_FINISHED:
      return {
        variant: "pending" as const,
        title: t("paymentReturn.pendingTitle"),
        description: t("paymentReturn.pendingDescription", { provider }),
      };
    case CurrentPaymentDtoStatus.CANCELLED:
    case CurrentPaymentDtoStatus.FAILED:
      return {
        variant: "error" as const,
        title: t("paymentReturn.errorTitle"),
        description: t("paymentReturn.errorDescription", { provider }),
      };
  }

  switch (confirmation.outcome) {
    case "success":
      return {
        variant: "success" as const,
        title: t("paymentReturn.successTitle"),
        description: t("paymentReturn.successDescription"),
      };
    case "pending":
      return {
        variant: "pending" as const,
        title: t("paymentReturn.pendingTitle"),
        description: t("paymentReturn.pendingDescription", { provider }),
      };
    case "failed":
      return {
        variant: "error" as const,
        title: t("paymentReturn.errorTitle"),
        description: t("paymentReturn.errorDescription", { provider }),
      };
    default:
      return {
        variant: "error" as const,
        title: t("paymentReturn.unknownTitle"),
        description: t("paymentReturn.unknownDescription", { provider }),
      };
  }
});

const statusIcon = computed(() => {
  if (display.value.variant === "success") return CircleCheck;
  if (display.value.variant === "pending") return Clock;
  return ShieldAlert;
});

const iconWrapperClass = computed(() =>
  display.value.variant === "error"
    ? "bg-destructive/10 text-destructive"
    : "bg-brand-fill/10 text-brand-ink",
);

onMounted(async () => {
  if (display.value.variant === "success") {
    toast.success(display.value.title);
  } else if (display.value.variant === "pending") {
    toast.info(display.value.title);
  } else {
    toast.error(display.value.title);
  }

  try {
    await paymentApi.finishCheckoutSession(
      confirmation.paymentProviderSessionId ?? confirmation.sessionId,
    );
  } catch (error) {
    console.warn(
      "Payment checkout session could not be finished by the backend:",
      error,
    );
  }

  startPaymentStatusPolling();
});

onUnmounted(() => {
  stopPaymentStatusPolling();
});

function startPaymentStatusPolling(): void {
  if (pollingInterval) return;
  pollingInterval = window.setInterval(pollPaymentStatus, 10_000);
  pollPaymentStatus();
}

function stopPaymentStatusPolling(): void {
  if (!pollingInterval) return;
  window.clearInterval(pollingInterval);
  pollingInterval = null;
}

function pollPaymentStatus(): void {
  customerSession
    .sync({ force: true })
    .then(() => {
      if (isTerminalPaymentStatus(latestPaymentStatus.value)) {
        stopPaymentStatusPolling();
      }
    })
    .catch(() => undefined);
}

function isTerminalPaymentStatus(status: string | undefined): boolean {
  return (
    status === CurrentPaymentDtoStatus.AUTHORISED ||
    status === CurrentPaymentDtoStatus.REFUNDED ||
    status === CurrentPaymentDtoStatus.CANCELLED ||
    status === CurrentPaymentDtoStatus.FAILED
  );
}
</script>
